<template>
  <div>
    <fieldset>
      <legend class="text-neutral-900 font-medium lg:font-bold">
        {{ isInputRadio ? $t('pets.concern') : $t('pets.pet-concerns') }}
      </legend>
      <span class="text-neutral-700 text-sm">
        {{ subHeader }}
      </span>
      <div
        v-if="loading"
        class="w-full flex justify-center items-center py-2"
      >
        <fa
          :icon="['fas', 'bone']"
          class="text-3xl text-ocean-900 fa-spin"
        />
      </div>
      <div
        v-else
        class="divide-y divide-gray-200 lg:divide-y-0 lg:grid lg:grid-cols-2 lg:gap-4 lg:mt-2"
      >
        <vetster-search-checkbox-input
          v-for="concern in collapsedPetConcerns"
          :key="concern.value"
          v-model="petConcerns"
          :value="concern.value"
          :disabled="isDisabled"
          :type="inputType"
          :icon="concern.icon"
          :label="getCxConcernLabel(concern.value)"
          :data-testid="`concern-btn-${concern.value}`"
        />
        <vetster-search-checkbox-input
          v-for="concern in expandedPetConcerns"
          v-show="showMoreOptions"
          :key="concern.value"
          v-model="petConcerns"
          :value="concern.value"
          :disabled="isDisabled"
          :type="inputType"
          :icon="concern.icon"
          :label="getCxConcernLabel(concern.value)"
          :data-testid="`concern-btn-${concern.value}`"
        />
      </div>
    </fieldset>
    <button
      v-if="!loading"
      data-testid="concerns-toggle-btn"
      type="button"
      class="mt-4 flex items-center space-x-2"
      @click="handleShowConcerns"
    >
      <span class="text-neutral-900 underline">
        {{ showMoreOptions ? $t('pets.show-less-concerns') : $t('pets.show-all-concerns') }}
      </span>
      <span
        :class="{ 'rotate-180': showMoreOptions }"
        class="h-full transition-transform duration-200 ease-in-out"
      >
        <fa
          :icon="['fal', 'chevron-down']"
          class="text-md text-neutral-900 fa-fw"
        />
      </span>
    </button>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import size from 'lodash/size';
import take from 'lodash/take';
import { mapGetters } from 'vuex';

import VetsterSearchCheckboxInput from '@/brand-components/VetsterSearchCheckboxInput';
import useSearchData from '@/composables/use-search-data';
import HasHealthConcerns from '@/mixins/HasHealthConcerns';
import ManagesSearchCookies from '@/mixins/ManagesSearchCookies';
import PetConcernsIcons from '@/mixins/PetConcerns';

library.add(faChevronDown);

export default {
  name: 'VetsterShowPetConcerns',

  components: { VetsterSearchCheckboxInput },

  mixins: [
    HasHealthConcerns,
    ManagesSearchCookies,
    PetConcernsIcons,
  ],

  setup() {
    const { searchConcernLimit } = useSearchData();

    return { searchConcernLimit };
  },

  data() {
    return {
      concernsSelected: [],
      collapsedPetConcerns: [],
      expandedPetConcerns: [],
      loading: true,
      showMoreOptions: false,
    };
  },

  computed: {
    ...mapGetters('petConcerns', ['getPrimaryPetConcerns']),

    ...mapGetters('search', ['getTempInputs']),

    inputType() {
      return this.isInputRadio ? 'radio' : 'checkbox';
    },

    isDisabled() {
      return !this.isInputRadio && size(this.concernsSelected) === this.searchConcernLimit;
    },

    isInputRadio() {
      return this.searchConcernLimit === 1;
    },

    petConcerns: {
      get() {
        return this.isInputRadio
          ? this.concernsSelected[0]
          : this.concernsSelected;
      },
      set(value) {
        this.concernsSelected = this.isInputRadio ? [value] : value;
      },
    },

    subHeader() {
      return this.isInputRadio
        ? this.$t('pets.please-select-your-main-concern')
        : this.$t('pets.please-select-up-to-max-concerns', { count: this.searchConcernLimit });
    },
  },

  watch: {
    concernsSelected(concerns) {
      this.$store.commit('search/setTempInputs', { concerns });
    },
    'getTempInputs.species_treated': {
      async handler(value) {
        if (typeof value === 'string') {
          await this.$store.dispatch('petConcerns/fetchPetConcerns', {
            pet_species: value,
          });
          this.collapsedPetConcerns = take(this.getPrimaryPetConcerns, 6);
          await this.highlightSelectedConcern();
          this.loading = false;
        }
      },
      immediate: true,
    },
  },

  methods: {
    handleShowConcerns() {
      this.showMoreOptions = !this.showMoreOptions;
    },

    async highlightSelectedConcern() {
      const concerns = this.getConcernsFromCookie();

      this.expandedPetConcerns = this.getPetConcerns.filter(
        (concern) => !this.collapsedPetConcerns.includes(concern),
      );

      if (concerns.length) {
        this.concernsSelected = concerns;
        if (this.expandedPetConcerns.some((concern) => concerns.includes(concern.value))) {
          this.showMoreOptions = true;
        }
        this.$store.commit('search/setTempInputs', { concerns });
      }
    },
  },
};
</script>
